/* The kit that keeps the lights on, and how to reach it.
 *
 * The iLO address went onto the device form and stopped there, which meant the
 * one field somebody needs while a server is not answering was behind an Edit
 * button, eleven rows down a panel, on a record you had to know to open. That
 * is the same as not having recorded it.
 *
 * A register is read far more than it is written, and this is the read surface
 * that was missing. Not a screen per device type — a switch is not looked up the
 * way a printer is — but one view of everything that is not somebody's laptop,
 * answering the question an incident actually starts with: what is here, where
 * is it, and how do I get into it when the usual way has stopped working.
 *
 * Endpoints are excluded rather than filtered out by default. Sixty-nine laptops
 * would bury the eleven things this exists for, and "which laptop does Rob have"
 * is what the Devices screen is for.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { countdown, daysSince } from '../../lib/format';
import { useOpenRecord } from '../../hooks/useOpenRecord';
import { subnetFor } from '../../lib/network';
import { INFRASTRUCTURE_TYPES } from '../../lib/deviceGroups';
import { DEVICE_SEEN, DEVICE_STATUS, deviceFields, valuesSeen } from '../../lib/registerFields';
import { DeviceDetail } from './DeviceDetail';
import { EditDrawer } from './EditDrawer';
import type { IDevice } from '../../models';
import { qk } from '../../query/keys';
import { BulkBar, useBulk } from './BulkBar';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  Select,
  SummaryTile,
  SummaryTiles,
  TableSkeleton,
  type Column,
} from '../ui';
import { Copyable } from './Copyable';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** A controller only these can have — the rest are reached at the address in
    the IP field, and offering a second one would invite two answers. */
const LIGHTS_OUT = ['Server', 'Storage'];

/** Long enough to mean something has stopped checking in rather than that
    somebody rebooted it. */
const QUIET_DAYS = 14;

export function InfrastructureScreen() {
  const svc = useServices();
  const { scope } = useDepotScope();
  const depots = useDepots();

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const networks = useQuery({ queryKey: qk.networks, queryFn: () => svc.networks.all() });

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });

  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<IDevice | null>(null);

  useOpenRecord(
    devices.data ?? [],
    (d, value) => d.id === value || d.tag.toLowerCase() === value.toLowerCase(),
    setSelected,
  );

  const qc = useQueryClient();

  /* Added here rather than on Devices. The type box offers only what belongs on
     this screen, so adding a switch cannot produce something that then turns up
     on a different list than the one you added it from. */
  /* The same three columns as Devices, because this is the same register seen
     through a filter and the bar should not behave differently depending on
     which list you reached it from. All three are Ledger's own, so a bulk
     write stands rather than being recomputed overnight. */
  const bulk = useBulk({
    rows: devices.data ?? [],
    idOf: (d) => d.id,
    nameOf: (d) => d.tag,
    fields: {
      loc: {
        label: 'Depot',
        options: depots.map((x) => ({ value: x.id, label: x.name })),
        note: 'Depot is yours — no sync has an opinion about where a machine lives.',
      },
      status: {
        label: 'Status',
        options: DEVICE_STATUS.map((x) => ({ value: x, label: x })),
        note: 'Status is yours and stands. Compliance and last seen come from Intune and are not offered here.',
      },
      ownership: {
        label: 'Owned or hired',
        options: [
          { value: 'Purchased', label: 'Purchased' },
          { value: 'Hired', label: 'Hired' },
        ],
        note: 'Yours. Changing it to Hired swaps the purchase and warranty dates for the hire contract on each record.',
      },
    },
    update: (id, patch) => svc.devices.update(id, patch as Partial<IDevice>),
    remove: (id) => svc.devices.remove(id),
    invalidate: [qk.devices, qk.audit],
  });

  const create = useMutation({

    meta: { ok: 'Device added' },
    mutationFn: (values: Record<string, unknown>) =>
      svc.devices.create(values as Omit<IDevice, 'id'>),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setAdding(false);
    },
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return scoped(devices.data ?? [], scope)
      .filter((d) => INFRASTRUCTURE_TYPES.includes(d.type))
      .filter((d) => (type === 'all' ? true : d.type === type))
      .filter(
        (d) =>
          !q ||
          [d.tag, d.make, d.model, d.ip, d.mgmtAddress, d.placement]
            .some((v) => (v ?? '').toLowerCase().includes(q)),
      )
      .sort((a, b) => a.type.localeCompare(b.type) || a.tag.localeCompare(b.tag));
  }, [devices.data, scope, type, query]);

  const types = useMemo(
    () => INFRASTRUCTURE_TYPES.filter((t) => (devices.data ?? []).some((d) => d.type === t)),
    [devices.data],
  );

  /* The gap that matters, and it is not "how many servers are there". A server
     whose controller nobody has recorded is one you cannot reach when it stops
     answering, which is the only moment you would want to. */
  const gaps = useMemo(() => {
    const canHave = rows.filter((d) => LIGHTS_OUT.includes(d.type));
    return {
      total: canHave.length,
      noMgmt: canHave.filter((d) => !d.mgmtAddress?.trim()).length,
      noCred: canHave.filter((d) => d.mgmtAddress?.trim() && !d.mgmtCredential?.trim()).length,
      quiet: rows.filter((d) => d.seen && daysSince(d.seen) > QUIET_DAYS).length,
    };
  }, [rows]);

  const columns: Column<IDevice>[] = [
    {
      key: 'tag',
      header: 'Asset',
      sortBy: (d) => d.tag,
      render: (d) => (
        <>
          <div className={s.cellMain}>{d.tag}</div>
          <div className={s.cellSub}>
            {d.make} {d.model}
          </div>
        </>
      ),
    },
    { key: 'type', header: 'Type', sortBy: (d) => d.type, render: (d) => <Badge tone="muted">{d.type}</Badge> },
    {
      key: 'where',
      header: 'Where',
      sortBy: (d) => `${d.loc}${d.placement ?? ''}`,
      render: (d) => (
        <>
          <div>{depots.find((x) => x.id === d.loc)?.name ?? d.loc}</div>
          {d.placement && <div className={s.cellSub}>{d.placement}</div>}
        </>
      ),
    },
    {
      key: 'ip',
      priority: 3,
      header: 'Address',
      sortBy: (d) => d.ip,
      render: (d) => {
        const subnet = subnetFor(d.ip ?? '', networks.data?.[d.loc]?.subnets ?? []);
        return d.ip ? (
          <>
            <Copyable value={d.ip} />
            {subnet && <div className={s.cellSub}>VLAN {subnet.vlan} · {subnet.name}</div>}
          </>
        ) : (
          <span className={s.cellSub}>—</span>
        );
      },
    },
    {
      key: 'mgmt',
      header: 'Out-of-band',
      sortBy: (d) => d.mgmtAddress ?? '',
      /* The column this screen exists for. It is deliberately not the last one:
         a field you need in an emergency does not belong at the right-hand edge
         where a narrow window puts it out of sight. */
      render: (d) => {
        if (!LIGHTS_OUT.includes(d.type)) return <span className={s.cellSub}>—</span>;
        if (!d.mgmtAddress) return <Badge tone="warn">Not recorded</Badge>;
        return (
          <>
            <Copyable value={d.mgmtAddress} />
            <div className={s.cellSub}>{d.mgmtType ?? 'management'}</div>
          </>
        );
      },
    },
    {
      key: 'cred',
      priority: 2,
      header: 'Login',
      render: (d) =>
        /* Named, not linked. The row itself opens the record now, and a link
           inside it would fight that — the panel carries the link through to
           Credentials, which is where following it belongs. */
        d.mgmtCredential ? (
          <span className={s.cellSub}>{d.mgmtCredential}</span>
        ) : d.mgmtAddress ? (
          <span className={s.cellSub}>Not recorded</span>
        ) : null,
    },
    {
      key: 'seen',
      priority: 2,
      header: 'Last seen',
      sortBy: (d) => d.seen,
      render: (d) =>
        d.seen ? (
          <span className={daysSince(d.seen) > QUIET_DAYS ? undefined : s.cellSub}>
            {countdown(d.seen)}
          </span>
        ) : (
          <span className={s.cellSub}>Never</span>
        ),
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Infrastructure</h1>
          <p>
            Everything that is not somebody's laptop — where it is, how to reach it, and how
            to get in when the usual way fails.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => setAdding(true)}>
          Add infrastructure
        </Button>
      </div>

      {devices.isError && <LoadError error={devices.error} what="devices" />}

      <SummaryTiles>
        <SummaryTile icon="ti-server-2" value={String(rows.length)} label="Pieces of kit" />
        <SummaryTile
          icon="ti-plug-connected-x"
          value={String(gaps.noMgmt)}
          label={`No out-of-band address (of ${gaps.total})`}
          tone={gaps.noMgmt > 0 ? 'warn' : 'ok'}
        />
        <SummaryTile
          icon="ti-key-off"
          value={String(gaps.noCred)}
          label="Reachable, no login recorded"
          tone={gaps.noCred > 0 ? 'warn' : 'ok'}
        />
        <SummaryTile
          icon="ti-antenna-bars-off"
          value={String(gaps.quiet)}
          label={`Quiet over ${QUIET_DAYS} days`}
          tone={gaps.quiet > 0 ? 'warn' : 'ok'}
        />
      </SummaryTiles>

      <div className={s.stack}>
        <Controls count={`${rows.length} shown`}>
          <SearchBox value={query} onChange={setQuery} placeholder="Tag, model or address…" />
          <Select
            label="Type"
            value={type}
            onChange={setType}
            options={[{ value: 'all', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))]}
          />
        </Controls>

      <BulkBar
        bulk={bulk}
        onDelete={() => {
          const n = bulk.ticked.size;
          return window.confirm(
            `Remove ${n} ${n === 1 ? 'record' : 'records'} from ${PRODUCT.name}?` +
              '\n\nThis deletes nothing in Intune or NinjaOne — it only removes the record here.' +
              '\n\nAnything still managed will be recreated at the next sync, without the purchase date, warranty or notes.',
          );
        }}
      />


        <Panel flush>
          {devices.isLoading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(d) => d.id}
              checked={bulk.ticked}
              onCheckedChange={bulk.setTicked}
              onRowClick={setSelected}
              selectedKey={selected?.id}
              initialSort={{ key: 'type', dir: 'asc' }}
              empty={
                <EmptyState icon="ti-server-off" title="No infrastructure recorded">
                  Anything on Devices with a type other than laptop, desktop, mobile or tablet
                  appears here.
                </EmptyState>
              }
            />
          )}
        </Panel>
      </div>


      {/* The record opens here rather than throwing you onto Devices. One
          machine, one panel — it just does not belong to one of the lists. */}
      <DeviceDetail device={selected} onClose={() => setSelected(null)} />

      <EditDrawer
        open={adding}
        record={null}
        initial={{ type: 'Server' }}
        /* Only the types this screen lists. Anything else added here would
           vanish from it the moment it was saved. */
        fields={deviceFields(depots, INFRASTRUCTURE_TYPES, valuesSeen(devices.data, DEVICE_SEEN))}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="piece of kit"
        icon="ti-server-2"
        onClose={() => setAdding(false)}
        onSave={(values) => create.mutate(values)}
        saving={create.isPending}
        error={create.error}
      />
    </>
  );
}
